import jalaliMoment from 'jalali-moment';

/**
 * Date formatting utilities with Jalali calendar support for Persian users
 */

export type DateFormat = 'short' | 'medium' | 'long' | 'full';

/**
 * Format a date according to the current language
 * Uses Jalali calendar for Persian (fa) and Gregorian for English (en)
 */
export function formatDate(
  date: Date | string | number,
  language: string,
  format: DateFormat = 'medium'
): string {
  const m = jalaliMoment(date);
  
  if (language === 'fa') {
    m.locale('fa');
    switch (format) {
      case 'short':
        return m.format('jYYYY/jMM/jDD');
      case 'medium':
        return m.format('jD jMMMM jYYYY');
      case 'long':
        return m.format('dddd، jD jMMMM jYYYY');
      case 'full':
        return m.format('dddd، jD jMMMM jYYYY - HH:mm');
      default:
        return m.format('jD jMMMM jYYYY');
    }
  } else {
    m.locale('en');
    switch (format) {
      case 'short':
        return m.format('YYYY/MM/DD');
      case 'medium':
        return m.format('MMMM D, YYYY');
      case 'long':
        return m.format('dddd, MMMM D, YYYY');
      case 'full':
        return m.format('dddd, MMMM D, YYYY - HH:mm');
      default:
        return m.format('MMMM D, YYYY');
    }
  }
}

/**
 * Format a relative time (e.g., "2 days ago", "۲ روز پیش")
 */
export function formatRelativeTime(
  date: Date | string | number,
  language: string
): string {
  const m = jalaliMoment(date);
  m.locale(language === 'fa' ? 'fa' : 'en');
  return m.fromNow();
}

/**
 * Get the current date formatted for the given language
 */
export function getCurrentDate(language: string, format: DateFormat = 'medium'): string {
  return formatDate(new Date(), language, format);
}

/**
 * Get the current year in the appropriate calendar
 */
export function getCurrentYear(language: string): string {
  const m = jalaliMoment();
  if (language === 'fa') {
    m.locale('fa');
    return m.format('jYYYY');
  }
  return m.format('YYYY');
}

/**
 * Parse a date string and return a Date object
 * Handles both Jalali and Gregorian date strings
 */
export function parseDate(dateString: string, language: string): Date {
  if (language === 'fa') {
    return jalaliMoment(dateString, 'jYYYY/jMM/jDD').toDate();
  }
  return jalaliMoment(dateString, 'YYYY/MM/DD').toDate();
}

/**
 * Format duration in a human-readable way
 */
export function formatDuration(seconds: number, language: string): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (language === 'fa') {
    const parts: string[] = [];
    if (hours > 0) parts.push(`${toPersianDigits(hours)} ساعت`);
    if (minutes > 0) parts.push(`${toPersianDigits(minutes)} دقیقه`);
    if (secs > 0 && hours === 0) parts.push(`${toPersianDigits(secs)} ثانیه`);
    return parts.join(' و ') || '۰ ثانیه';
  } else {
    const parts: string[] = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (secs > 0 && hours === 0) parts.push(`${secs}s`);
    return parts.join(' ') || '0s';
  }
}

/**
 * Convert English digits to Persian digits
 */
export function toPersianDigits(num: number | string): string {
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return String(num).replace(/[0-9]/g, (d) => persianDigits[parseInt(d)]);
}

/**
 * Convert Persian digits to English digits
 */
export function toEnglishDigits(str: string): string {
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return str.replace(/[۰-۹]/g, (d) => String(persianDigits.indexOf(d)));
}
